import React, { useEffect, useRef, useState } from 'react';

/**
 * Cockpit Tabs — quiet text tabs with a sliding rust underline.
 */
export interface TabsProps {
  tabs: string[];
  active: string;
  onChange?: (tab: string) => void;
  style?: React.CSSProperties;
}

export default function Tabs({ tabs, active, onChange, style }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [bar, setBar] = useState({ left: 0, width: 0 });

  const tabsKey = tabs.join('|');
  useEffect(() => {
    const el = refs.current[active];
    if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth });
  }, [active, tabsKey]);

  return (
    <div
      className="ck-tabs"
      style={{
        position: 'relative',
        display: 'flex',
        gap: 'var(--space-1)',
        borderBottom: '1px solid var(--border-hairline)',
        ...style,
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab === active;
        return (
          <button
            key={tab}
            ref={(el) => (refs.current[tab] = el)}
            onClick={() => onChange && onChange(tab)}
            className="hover-wash pressable"
            style={{
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-xs) var(--radius-xs) 0 0',
              padding: '9px 12px',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-base)',
              fontWeight: (isActive ? 'var(--weight-semibold)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
              color: isActive ? 'var(--text-body)' : 'var(--text-muted)',
              cursor: 'pointer',
              transition: 'color var(--dur-base) var(--ease-out)',
            }}
          >
            {tab}
          </button>
        );
      })}
      <span
        style={{
          position: 'absolute',
          bottom: '-1px',
          left: bar.left,
          width: bar.width,
          height: '2px',
          borderRadius: '1px',
          background: 'var(--accent)',
          transition: 'left var(--dur-base) var(--ease-out), width var(--dur-base) var(--ease-out)',
        }}
      />
    </div>
  );
}
